import { areAskInputDraftContextsEqual } from '#pplx/frontend/apps/ask/ask-input/ask-input-drafts/types.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
const SAVE_DELAY_MS = 300;
export function createAskInputDraftSession({ repository, context, onError, createUuid = safeRandomUUID, now = Date.now, saveDelayMs = SAVE_DELAY_MS, }) {
    const sessionState = {
        activeDraft: undefined,
        pendingDraft: undefined,
        saveTimer: undefined,
        writeQueue: Promise.resolve(),
        draftsSnapshot: undefined,
        refreshRequestId: 0,
    };
    const listeners = new Set();
    const clearSaveTimer = () => {
        if (sessionState.saveTimer === undefined)
            return;
        clearTimeout(sessionState.saveTimer);
        sessionState.saveTimer = undefined;
    };
    const enqueue = (write) => {
        const runWrite = async () => {
            try {
                await write();
            }
            catch (error) {
                onError(error);
            }
        };
        sessionState.writeQueue = sessionState.writeQueue.then(runWrite, runWrite);
        return sessionState.writeQueue;
    };
    const refreshDrafts = async () => {
        const requestId = ++sessionState.refreshRequestId;
        try {
            const activeDraftUuid = sessionState.activeDraft?.uuid;
            const drafts = (await repository.listDrafts()).filter((draft) => areAskInputDraftContextsEqual(draft.context, context) && draft.uuid !== activeDraftUuid);
            if (requestId !== sessionState.refreshRequestId)
                return;
            sessionState.draftsSnapshot = drafts;
            listeners.forEach((listener) => listener());
        }
        catch (error) {
            onError(error);
        }
    };
    const enqueueAndRefresh = (write) => enqueue(write).then(refreshDrafts);
    const commitPendingDraft = () => {
        clearSaveTimer();
        if (!sessionState.pendingDraft)
            return sessionState.writeQueue;
        const draft = sessionState.pendingDraft;
        sessionState.pendingDraft = undefined;
        return enqueueAndRefresh(() => repository.upsertDraft(draft));
    };
    const discardActiveDraft = () => {
        clearSaveTimer();
        sessionState.pendingDraft = undefined;
        const uuid = sessionState.activeDraft?.uuid;
        sessionState.activeDraft = undefined;
        if (!uuid)
            return sessionState.writeQueue;
        return enqueueAndRefresh(() => repository.deleteDraft(uuid));
    };
    const deleteDraft = (uuid) => {
        if (sessionState.activeDraft?.uuid === uuid)
            return discardActiveDraft();
        return enqueueAndRefresh(() => repository.deleteDraft(uuid));
    };
    const clearDrafts = async () => {
        const draftUuids = sessionState.draftsSnapshot?.map((draft) => draft.uuid) ?? [];
        if (draftUuids.length === 0)
            return true;
        let didClearDrafts = false;
        await enqueue(async () => {
            await Promise.all(draftUuids.map((uuid) => repository.deleteDraft(uuid)));
            didClearDrafts = true;
        });
        await refreshDrafts();
        return didClearDrafts;
    };
    return {
        updateDraft: (snapshot) => {
            if (!snapshot?.text.trim()) {
                void discardActiveDraft();
                return;
            }
            const updatedAtMs = now();
            sessionState.activeDraft ??= { uuid: createUuid(), createdAtMs: updatedAtMs };
            sessionState.pendingDraft = {
                schemaVersion: 1,
                uuid: sessionState.activeDraft.uuid,
                context,
                snapshot,
                createdAtMs: sessionState.activeDraft.createdAtMs,
                updatedAtMs,
            };
            clearSaveTimer();
            sessionState.saveTimer = setTimeout(() => void commitPendingDraft(), saveDelayMs);
        },
        activateDraft: async (draft) => {
            if (!areAskInputDraftContextsEqual(draft.context, context)) {
                throw new Error('Cannot activate a draft from another input context');
            }
            await commitPendingDraft();
            sessionState.activeDraft = { uuid: draft.uuid, createdAtMs: draft.createdAtMs };
            await refreshDrafts();
        },
        discardActiveDraft,
        deleteDraft,
        clearDrafts,
        subscribe: (listener) => {
            listeners.add(listener);
            void sessionState.writeQueue.then(refreshDrafts);
            return () => {
                listeners.delete(listener);
            };
        },
        getDraftsSnapshot: () => sessionState.draftsSnapshot,
        flush: commitPendingDraft,
    };
}
//# sourceMappingURL=askInputDraftSession.js.map